import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Landmark, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(res.user.role === "admin" ? "/admin" : "/app");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="app-bg min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 rounded-2xl overflow-hidden glass shadow-2xl animate-fade-up">
        {/* Left brand panel */}
        <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-slate-900 to-slate-950 border-r border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center">
              <Landmark className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="font-heading text-xl font-extrabold">Paisapaid</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Own Brand Loan Agency</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight">
              Lead & Telecalling <span className="text-primary">Command Center</span>
            </h1>
            <p className="mt-4 text-muted-foreground text-sm leading-relaxed max-w-sm">
              Capture leads, auto-assign via round-robin, track every call and follow-up,
              and convert faster — all from one secure platform.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" /> Enterprise-grade security & role-based access
          </div>
        </div>

        {/* Right form */}
        <div className="p-8 sm:p-12 bg-card">
          <h2 className="font-heading text-2xl font-bold">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Access your Paisapaid dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@paisapaid.in" data-testid="login-email-input"
                className="bg-slate-900/60 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input"
                className="bg-slate-900/60 border-slate-700"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
